import React, { useRef, useEffect, useState, useLayoutEffect } from 'react';
import { useSpring, animated } from 'react-spring';
import debounce from 'lodash/debounce';

import * as Types from 'types';
import { ArtworkTitle } from 'components/artworks/ArtworkTitle';

type Props = {
  interior: 'loft' | 'moscow' | 'mac' | 'lounge';
  artwork: Types.Artwork;
  showArtistName?: boolean;
  hasAnimation?: boolean;
};

/**
 * Ratio to client's screen height when title should appear
 */
const showRatio = 0.6;

export const DeviceArtworkTitle: React.FC<Props> = ({
  interior,
  artwork,
  showArtistName = false,
  hasAnimation = true,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [elementY, setElementY] = useState(0);
  const [show, setShow] = useState(false);

  useLayoutEffect(() => {
    const storeElementY = () => {
      const { current } = ref;
      const y = current ? current.getBoundingClientRect().top + window.scrollY : 0;
      if (!Number.isNaN(y)) {
        setElementY(y);
      }
    };
    storeElementY();
    window.addEventListener('resize', storeElementY);
    return () => window.removeEventListener('resize', storeElementY);
  }, [ref.current]);

  useEffect(() => {
    const storeScrollPosition = debounce(() => {
      const visibleDistance = window.innerHeight * showRatio;
      const yStart = elementY - visibleDistance;
      const yEnd = yStart + visibleDistance;

      const shouldShow = window.scrollY > yStart && window.scrollY < yEnd;
      setShow(shouldShow);
    });
    storeScrollPosition();
    window.addEventListener('scroll', storeScrollPosition, { passive: true });
    return () => window.removeEventListener('scroll', storeScrollPosition);
  }, [ref.current]);

  const spring = useSpring({
    number: show ? 110 : !hasAnimation ? 110 : 0,
    from: { number: !hasAnimation ? 110 : 0 },
  });

  const light = interior === 'moscow' || interior === 'lounge';

  return (
    <animated.div
      ref={ref}
      className={`pos-abs pt-1 ${light ? 'text-light' : ''}`}
      style={{
        // zIndex: 1,
        bottom: 0,
        // Since we are using transform x -110% we need to make
        // width is the same for all elements otherwise
        // travelled distance and gap will be uneven
        width: 300,
        transform:
          interior === 'mac'
            ? spring.number.interpolate((number) => `translate3d(-${number}%, 0, 0)`)
            : spring.number.interpolate((number) => `translate3d(0, ${number}%, 0)`),
      }}
    >
      <ArtworkTitle
        className={interior === 'mac' ? 'text-right' : ''}
        artwork={artwork}
        maxlength={40}
        small
        showArtistName={showArtistName}
      />
    </animated.div>
  );
};
